import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { C } from '../albumColors';
import useIsMobile from '../hooks/useIsMobile';

/**
 * Diálogo de confirmación reutilizable.
 *
 * Layouts:
 *  - Mobile: bottom-sheet con backdrop.
 *  - Desktop: modal centrado 360px con backdrop.
 *
 * variant: 'primary' | 'danger' — controla el color del botón de confirmación.
 */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  cancelLabel,
  variant = 'primary',
  onConfirm,
  onCancel,
  busy = false,
  theme,
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const confirmRef = useRef(null);
  // Chrome look — stays neutral white/blue regardless of album theme so the
  // confirm dialog matches the rest of the app instead of inheriting the
  // emerald variant of the album.
  const surfaceBg     = 'var(--md-sys-color-surface, #ffffff)';
  const surfaceBorder = 'var(--md-sys-color-outline-variant, rgba(0,0,0,0.08))';
  const headerText    = 'var(--md-sys-color-on-surface, #111827)';
  const mutedText     = 'var(--md-sys-color-on-surface-variant, #4b5563)';
  const accent        = 'var(--md-sys-color-primary, #2563eb)';

  useEffect(() => {
    if (!open) return;
    confirmRef.current?.focus?.();
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel?.();
      if (e.key === 'Enter') onConfirm?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onCancel, onConfirm]);

  if (!open) return null;

  const confirmBg = variant === 'danger' ? C.error : accent;
  const wrapperClass = isMobile
    ? 'absolute bottom-0 inset-x-0 rounded-t-2xl pb-4'
    : 'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[min(380px,92vw)] rounded-2xl';

  return (
    <div
      className="fixed inset-0 z-[120]"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="cm-confirm-title"
    >
      <div
        className="absolute inset-0 bg-black/45"
        onClick={busy ? undefined : onCancel}
        aria-hidden="true"
      />
      <div
        className={`${wrapperClass} shadow-2xl flex flex-col`}
        style={{
          backgroundColor: surfaceBg,
          border: `1px solid ${surfaceBorder}`,
          animation: isMobile ? 'cm-confirm-up 180ms ease-out' : 'cm-confirm-pop 160ms ease-out',
        }}
      >
        <style>{`
          @keyframes cm-confirm-up { from { transform: translateY(100%); } to { transform: translateY(0); } }
          @keyframes cm-confirm-pop { from { opacity: 0; transform: translate(-50%, -45%); } to { opacity: 1; transform: translate(-50%, -50%); } }
        `}</style>

        <div className="px-5 pt-5 pb-3">
          <h3 id="cm-confirm-title" className="text-base font-black tracking-tight" style={{ color: headerText }}>
            {title}
          </h3>
          {body && (
            <p className="mt-2 text-[13px] leading-relaxed" style={{ color: mutedText }}>
              {body}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 px-5 pb-2 pt-1">
          <button
            onClick={onCancel}
            disabled={busy}
            className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50"
            style={{ color: mutedText, border: `1px solid ${surfaceBorder}`, backgroundColor: 'transparent' }}
          >
            {cancelLabel || t('Volver')}
          </button>
          <button
            ref={confirmRef}
            onClick={onConfirm}
            disabled={busy}
            className="flex-1 py-2.5 rounded-lg text-xs font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ color: 'white', backgroundColor: confirmBg }}
          >
            {busy ? t('Procesando...') : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConfirmDialog;
